import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import ErrorBoundary from '%app/js/common/components/ErrorBoundary';
import Header from '../Header';
import { HEADER_HEIGHT_XS, HEADER_HEIGHT_MD } from '../Header/constants';
import { SearchPanel } from '../SearchPanel';
import { useHeaderOverlayContext } from '../Header/HeaderOverlayContext';
import Footer from '../Footer';
import CookieConsentBanner from '../CookieConsent';
import PageNotice from '../PageNotice';
import ScrollToTop from '../ScrollToTop';
import RefreshAccountOnNavigation from '../RefreshAccountOnNavigation';
import isCheckoutRoutePathname from '../../services/isCheckoutRoutePathname';
import { layoutTranslationsShape, footerLinksShape } from '../Footer/propTypes';

const SHELL_SX = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
};

const MAIN_SX = {
    flex: '1 1 auto',
};

function buildSearchContainerSx(isSearchOpen) {
    return {
        display: { xs: isSearchOpen ? 'block' : 'none', lg: 'block' },
        position: { xs: 'fixed', lg: 'relative' },
        top: { xs: HEADER_HEIGHT_XS, md: HEADER_HEIGHT_MD, lg: 'auto' },
        left: { xs: 0, lg: 'auto' },
        right: { xs: 0, lg: 'auto' },
        maxHeight: {
            xs: `calc(100vh - ${HEADER_HEIGHT_XS}px)`,
            md: `calc(100vh - ${HEADER_HEIGHT_MD}px)`,
            lg: 'none',
        },
        overflowY: { xs: 'auto', lg: 'visible' },
        bgcolor: { xs: 'background.default', lg: 'transparent' },
        zIndex: theme => theme.zIndex.appBar - 1,
    };
}

function Layout({
    children, translations, footerLinks,
}) {
    const overlay = useHeaderOverlayContext();
    const location = useLocation();
    const onCheckout = isCheckoutRoutePathname(location.pathname);

    const searchContainerSx = useMemo(
        () => buildSearchContainerSx(overlay.isSearchOpen),
        [overlay.isSearchOpen],
    );

    return (
        <Box sx={SHELL_SX} data-testid="redesign-shell">
            <ScrollToTop />
            <RefreshAccountOnNavigation />
            <Header
                translations={translations}
                mobileSearchEnabled
                checkoutMode={onCheckout}
            />
            {onCheckout ? null : (
                <Box sx={searchContainerSx} data-testid="search-panel-container">
                    <ErrorBoundary fallback={null}>
                        <SearchPanel onSubmit={overlay.close} />
                    </ErrorBoundary>
                </Box>
            )}
            <ErrorBoundary fallback={null}>
                <PageNotice />
            </ErrorBoundary>
            <Box component="main" sx={MAIN_SX} data-testid="redesign-main">
                <ErrorBoundary>
                    {children}
                </ErrorBoundary>
            </Box>
            <CookieConsentBanner />
            {onCheckout ? null : (
                <Footer translations={translations} footerLinks={footerLinks} />
            )}
        </Box>
    );
}

Layout.propTypes = {
    children: PropTypes.node.isRequired,
    translations: layoutTranslationsShape.isRequired,
    footerLinks: footerLinksShape.isRequired,
};

export default Layout;
export { buildSearchContainerSx };
